import { ComponentProps, ReactNode, useEffect } from 'react';

import classNames from 'classnames';

import useFloatingContainer, { UseFloatingContainerOptions } from '../FloatingContainer/useFloatingContainer';

import styles from './index.module.scss';

export type PopoverProps = Omit<ComponentProps<'div'>, 'children' | 'content'> & {
  children: (trigger: () => void, close: () => void) => ReactNode;
  content: ReactNode | ((close: () => void) => ReactNode);
  initialVisible?: boolean;
  onHide?: () => void;
  onShow?: () => void;
  showArrow?: boolean;
  side?: UseFloatingContainerOptions['placement'];
};

export default function Popover({
  children,
  className,
  content,
  initialVisible = false,
  onHide,
  onShow,
  showArrow = true,
  side = 'bottom',
  ...props
}: PopoverProps) {
  const { FloatingContainer, isOpen, setOpen, getReferenceProps } = useFloatingContainer({
    open: initialVisible,
    placement: side,
    enableArrow: showArrow,
  });

  const showPopover = () => setOpen(true);
  const hidePopover = () => setOpen(false);

  useEffect(() => {
    if (isOpen) {
      onShow?.();
      return;
    }

    onHide?.();
  }, [onHide, onShow, isOpen]);

  return (
    <>
      <span className={styles.popoverContainer} {...getReferenceProps()}>
        {children(showPopover, hidePopover)}
      </span>

      <FloatingContainer>
        <div className={classNames(styles.popover, className)} {...props}>
          {content instanceof Function ? content(hidePopover) : content}
        </div>
      </FloatingContainer>
    </>
  );
}
